import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trophy, Download, FileText, TrendingUp, TrendingDown, Minus, Zap, Award, Target, BarChart3, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend, LineChart, Line } from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PrintableReport from "../components/PrintableReport";

const GRADE_VALUES = { 'A': 4, 'B': 3, 'C': 2, 'D': 1 };
const GRADE_COLORS = {
  'A': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', fill: '#10b981' },
  'B': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', fill: '#3b82f6' },
  'C': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', fill: '#f59e0b' },
  'D': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', fill: '#f97316' }
};

const GradeBadge = ({ grade, size = 'md' }) => {
  const colors = GRADE_COLORS[grade] || GRADE_COLORS['C'];
  const sizeClasses = size === 'lg' ? 'w-14 h-14 text-2xl' : 'w-10 h-10 text-lg';
  return (
    <span className={`inline-flex items-center justify-center ${sizeClasses} rounded-xl font-bold ${colors.bg} ${colors.text} ${colors.border} border-2 shadow-sm`}>
      {grade}
    </span>
  );
};

const TrendIndicator = ({ current, previous }) => {
  if (!previous) return null;
  const currentVal = GRADE_VALUES[current] || 0;
  const prevVal = GRADE_VALUES[previous] || 0;
  const diff = currentVal - prevVal;

  if (diff > 0) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (diff < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [quarterlyReports, setQuarterlyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem('userRole');
  const printRef = useRef();
  const [printingReport, setPrintingReport] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [studentRes, quarterlyRes] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/reports/quarterly/${id}`)
      ]);
      setStudent(studentRes.data);
      setQuarterlyReports(quarterlyRes.data);
    } catch (error) {
      toast.error("Failed to load student data");
    } finally {
      setLoading(false);
    }
  };

  const getLatestReport = () => quarterlyReports[0] || null;
  const getPreviousReport = (report = null) => {
    if (!report) return quarterlyReports[1] || null;
    const reportIndex = quarterlyReports.findIndex(r => r.id === report.id);
    return reportIndex >= 0 && reportIndex < quarterlyReports.length - 1 ? quarterlyReports[reportIndex + 1] : null;
  };

  const getRadarData = (report) => {
    if (!report) return [];
    return [
      { skill: 'Passing', value: GRADE_VALUES[report.grades.performance.passing], fullMark: 4 },
      { skill: 'Shooting', value: GRADE_VALUES[report.grades.performance.shooting], fullMark: 4 },
      { skill: 'Dribbling', value: GRADE_VALUES[report.grades.performance.dribbling], fullMark: 4 },
      { skill: 'Heading', value: GRADE_VALUES[report.grades.performance.heading], fullMark: 4 },
      { skill: 'Ball Control', value: GRADE_VALUES[report.grades.performance.ball_control], fullMark: 4 },
      { skill: 'Match Play', value: GRADE_VALUES[report.grades.performance.match_play], fullMark: 4 },
    ];
  };

  const getComparisonData = (report = null) => {
    const current = report || getLatestReport();
    const previous = getPreviousReport(report);
    if (!current) return [];

    const skills = ['passing', 'shooting', 'dribbling', 'heading', 'ball_control', 'match_play'];
    return skills.map(skill => ({
      skill: skill.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      current: GRADE_VALUES[current.grades.performance[skill]],
      previous: previous ? GRADE_VALUES[previous.grades.performance[skill]] : 0,
    }));
  };

  const getTrendData = () => {
    return quarterlyReports.slice(0, 6).reverse().map((report, idx) => {
      const skills = report.grades.performance;
      const avg = (GRADE_VALUES[skills.passing] + GRADE_VALUES[skills.shooting] +
        GRADE_VALUES[skills.dribbling] + GRADE_VALUES[skills.heading] +
        GRADE_VALUES[skills.ball_control] + GRADE_VALUES[skills.match_play]) / 6;
      return {
        quarter: `${report.quarter.split(' ')[0]} ${report.year}`,
        score: parseFloat(avg.toFixed(2)),
        regularity: GRADE_VALUES[report.grades.regularity],
        discipline: GRADE_VALUES[report.grades.discipline],
      };
    });
  };

  const formatSkillLabel = (skill) => skill.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const getAverageSkillScore = (report) => {
    if (!report) return 0;
    const values = Object.values(report.grades.performance).map((grade) => GRADE_VALUES[grade] || 0);
    if (!values.length) return 0;
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    return parseFloat(avg.toFixed(2));
  };

  const getSkillFocus = (report) => {
    if (!report) return { strengths: [], focus: [] };
    const skills = report.grades.performance;
    return Object.entries(skills).reduce(
      (acc, [skill, grade]) => {
        if (grade === 'A') acc.strengths.push(formatSkillLabel(skill));
        if (grade === 'D') acc.focus.push(formatSkillLabel(skill));
        return acc;
      },
      { strengths: [], focus: [] },
    );
  };

  const getOverallGrade = (report) => {
    if (!report) return 'N/A';
    const skills = report.grades.performance;
    const avg = (GRADE_VALUES[skills.passing] + GRADE_VALUES[skills.shooting] +
      GRADE_VALUES[skills.dribbling] + GRADE_VALUES[skills.heading] +
      GRADE_VALUES[skills.ball_control] + GRADE_VALUES[skills.match_play]) / 6;
    if (avg >= 3.5) return 'A';
    if (avg >= 2.5) return 'B';
    if (avg >= 1.5) return 'C';
    return 'D';
  };

  const getInsights = () => {
    const latest = getLatestReport();
    const previous = getPreviousReport();
    if (!latest) return [];

    const insights = [];
    const skills = latest.grades.performance;

    // Find strengths
    const strengths = Object.entries(skills).filter(([_, grade]) => grade === 'A');
    if (strengths.length > 0) {
      insights.push({
        type: 'strength',
        icon: <Award className="w-5 h-5 text-emerald-500" />,
        title: 'Top Strengths',
        text: `Excellent performance in ${strengths.map(([s]) => s.replace('_', ' ')).join(', ')}.`
      });
    }

    // Find areas for improvement
    const improvements = Object.entries(skills).filter(([_, grade]) => grade === 'D');
    if (improvements.length > 0) {
      insights.push({
        type: 'improvement',
        icon: <Target className="w-5 h-5 text-amber-500" />,
        title: 'Focus Areas',
        text: `Needs more practice in ${improvements.map(([s]) => s.replace('_', ' ')).join(', ')}.`
      });
    }

    // Compare with previous
    if (previous) {
      let improved = 0, declined = 0;
      Object.keys(skills).forEach(skill => {
        const diff = GRADE_VALUES[skills[skill]] - GRADE_VALUES[previous.grades.performance[skill]];
        if (diff > 0) improved++;
        if (diff < 0) declined++;
      });

      if (improved > declined) {
        insights.push({
          type: 'progress',
          icon: <TrendingUp className="w-5 h-5 text-emerald-500" />,
          title: 'Great Progress!',
          text: `Improved in ${improved} skill${improved > 1 ? 's' : ''} compared to last quarter.`
        });
      } else if (declined > improved) {
        insights.push({
          type: 'attention',
          icon: <TrendingDown className="w-5 h-5 text-red-500" />,
          title: 'Needs Attention',
          text: `Performance dipped in ${declined} area${declined > 1 ? 's' : ''}. Consider extra practice sessions.`
        });
      }
    }

    return insights;
  };

  const generatePDF = async (report) => {
    toast.info("Generating PDF...");
    setPrintingReport(report);
    // Allow React to render the hidden PrintableReport and all charts to fully render
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (!printRef.current) {
      toast.error("Failed to generate PDF");
      setPrintingReport(null);
      return;
    }
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const marginTop = 10; // mm
      const marginBottom = 10; // mm
      const marginLeft = 10; // mm
      const marginRight = 10; // mm
      const pdfWidth = pageW - marginLeft - marginRight;
      
      // Get all print sections and capture individually for smart page breaking
      const sections = printRef.current.querySelectorAll('.print-section');
      if (sections.length === 0) {
        // Fallback: capture entire report if no sections found
        const canvas = await html2canvas(printRef.current, {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', marginLeft, marginTop, pdfWidth, imgHeight);
      } else {
        // Smart page breaking: capture sections and add to PDF intelligently
        let currentY = marginTop;
        
        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];
          const canvas = await html2canvas(section, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          });
          
          const sectionHeight = (canvas.height * pdfWidth) / canvas.width;
          const sectionPadding = 8; // mm spacing between sections
          
          // Check if section fits on current page
          if (currentY + sectionHeight > pageH - marginBottom) {
            // Section doesn't fit, add new page
            pdf.addPage();
            currentY = marginTop;
          }
          
          // Add section to PDF
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', marginLeft, currentY, pdfWidth, sectionHeight);
          currentY += sectionHeight + sectionPadding;
        }
      }

      pdf.save(`${student?.name}_${report.quarter}_${report.year}_Report.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error("Failed to generate PDF");
    } finally {
      setPrintingReport(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-slate-600 text-lg">Loading...</div>
      </div>
    );
  }

  const latestReport = getLatestReport();
  const previousReport = getPreviousReport();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Premium Header */}
      <nav className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
                className="hover:bg-white/10 text-white -ml-2 rounded-xl"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src="/gfa-logo.png?v=5" alt="GFA Logo" className="w-10 h-10 object-contain rounded-full bg-white p-0.5" />
              <div>
                <h1 className="text-xl font-bold" data-testid="student-detail-name">{student?.name}</h1>
                <p className="text-sm text-slate-400">Age: {student?.age} • {student?.skill_level}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-emerald-500/20 px-4 py-2 rounded-full border border-white/10">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-white/90">STADIA</span>
              </div>
              {userRole === 'coach' && (
                <Button
                  onClick={() => navigate(`/performance/add/${id}`)}
                  data-testid="add-performance-btn"
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold shadow-lg rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Report
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {quarterlyReports.length === 0 ? (
          <Card className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm">
            <CardContent className="py-20 text-center">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl flex items-center justify-center mb-6">
                <Trophy className="w-12 h-12 text-slate-400" />
              </div>
              <p className="text-xl text-slate-600" data-testid="no-performance-message">
                {userRole === 'coach'
                  ? 'No quarterly reports yet. Create the first assessment!'
                  : 'Your coach will add quarterly reports soon.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="shadow-lg border-0 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                <CardHeader className="pb-2">
                  <p className="text-emerald-100 text-sm font-medium">Overall Grade</p>
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-5xl font-bold">{getOverallGrade(latestReport)}</CardTitle>
                    {previousReport && <TrendIndicator current={getOverallGrade(latestReport)} previous={getOverallGrade(previousReport)} />}
                  </div>
                </CardHeader>
              </Card>

              <Card className="shadow-lg border-0 rounded-2xl bg-white">
                <CardHeader className="pb-2">
                  <p className="text-slate-500 text-sm font-medium">Total Reports</p>
                  <CardTitle className="text-4xl font-bold text-slate-900">{quarterlyReports.length}</CardTitle>
                </CardHeader>
              </Card>

              <Card className="shadow-lg border-0 rounded-2xl bg-white">
                <CardHeader className="pb-2">
                  <p className="text-slate-500 text-sm font-medium">Regularity</p>
                  <div className="flex items-center gap-2">
                    <GradeBadge grade={latestReport?.grades.regularity} size="lg" />
                    {previousReport && <TrendIndicator current={latestReport?.grades.regularity} previous={previousReport?.grades.regularity} />}
                  </div>
                </CardHeader>
              </Card>

              <Card className="shadow-lg border-0 rounded-2xl bg-white">
                <CardHeader className="pb-2">
                  <p className="text-slate-500 text-sm font-medium">Discipline</p>
                  <div className="flex items-center gap-2">
                    <GradeBadge grade={latestReport?.grades.discipline} size="lg" />
                    {previousReport && <TrendIndicator current={latestReport?.grades.discipline} previous={previousReport?.grades.discipline} />}
                  </div>
                </CardHeader>
              </Card>
            </div>

            {/* AI Insights */}
            {getInsights().length > 0 && (
              <Card className="shadow-lg border-0 rounded-2xl bg-gradient-to-r from-violet-50 to-purple-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-violet-900">
                    <Sparkles className="w-5 h-5 text-violet-500" />
                    Performance Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {getInsights().map((insight, idx) => (
                      <div key={idx} className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          {insight.icon}
                          <span className="font-semibold text-slate-800">{insight.title}</span>
                        </div>
                        <p className="text-sm text-slate-600">{insight.text}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Radar Chart */}
              <Card className="shadow-lg border-0 rounded-2xl bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-emerald-500" />
                    Skills Overview
                  </CardTitle>
                  <p className="text-sm text-slate-500">Current quarter performance</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getRadarData(latestReport)}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 4]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <Radar name="Skills" dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.5} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Comparison Chart */}
              {previousReport && (
                <Card className="shadow-lg border-0 rounded-2xl bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      Quarter Comparison
                    </CardTitle>
                    <p className="text-sm text-slate-500">Current vs Previous quarter</p>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getComparisonData(latestReport)} barGap={8}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 11 }} />
                        <YAxis domain={[0, 4]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}
                          formatter={(value) => [['D', 'C', 'B', 'A'][value - 1] || 'N/A', '']}
                        />
                        <Legend />
                        <Bar dataKey="previous" name="Previous" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="current" name="Current" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Trend Chart */}
            {quarterlyReports.length > 1 && (
              <Card className="shadow-lg border-0 rounded-2xl bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-violet-500" />
                    Performance Trend
                  </CardTitle>
                  <p className="text-sm text-slate-500">Progress over time</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={getTrendData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="quarter" tick={{ fill: '#64748b', fontSize: 11 }} />
                      <YAxis domain={[0, 4]} tick={{ fill: '#94a3b8', fontSize: 11 }} ticks={[1, 2, 3, 4]} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}
                        formatter={(value) => [value.toFixed(2), '']}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="score" name="Avg Skills" stroke="#10b981" strokeWidth={3} dot={{ r: 5, fill: '#10b981' }} />
                      <Line type="monotone" dataKey="regularity" name="Regularity" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="discipline" name="Discipline" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Recent Reports */}
            <Card className="shadow-lg border-0 rounded-2xl bg-white">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Quarterly Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {quarterlyReports.map((report, index) => (
                    <div key={report.id} className="p-5 hover:bg-slate-50 transition-colors" data-testid={`quarterly-report-${index}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                            <span className="text-lg font-bold text-emerald-700">{report.quarter.split(' ')[0]}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{report.quarter} {report.year}</p>
                            <p className="text-sm text-slate-500">{format(new Date(report.date), 'MMMM dd, yyyy')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="hidden md:flex items-center gap-2">
                            {Object.entries(report.grades.performance).slice(0, 3).map(([skill, grade]) => (
                              <GradeBadge key={skill} grade={grade} />
                            ))}
                            <span className="text-slate-400">...</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => generatePDF(report)}
                            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`download-pdf-${index}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                        </div>
                      </div>
                      {report.remarks && (
                        <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 ml-16">
                          "{report.remarks}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {/* Hidden printable report – captured by html2canvas */}
      <div style={{ position: 'absolute', top: -10000, left: -10000, zIndex: -1, minWidth: '794px' }}>
        {printingReport && (
          <PrintableReport
            ref={printRef}
            student={student}
            report={printingReport}
            radarData={getRadarData(printingReport)}
            comparisonData={getComparisonData(printingReport)}
            previousReport={getPreviousReport(printingReport)}
          />
        )}
      </div>
    </div>
  );
}
